// Focus trap for modal/dialog surfaces (popup-modal, cart-drawer, search-dropdown, header-drawer).
// One trap per surface, mutually exclusive in this theme — no stack management.

(function () {
    var FOCUSABLE_SELECTOR = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'audio[controls]',
        'video[controls]',
        '[contenteditable]:not([contenteditable="false"])',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    var INERT_FLAG = 'data-metta-trap-inert';

    function isVisible(el) {
        if (!el) return false;
        if (el.hasAttribute('disabled')) return false;
        if (el.getAttribute('aria-hidden') === 'true') return false;
        var rects = el.getClientRects();
        if (!rects.length) return false;
        if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
        var cs = window.getComputedStyle(el);
        if (cs.visibility === 'hidden' || cs.visibility === 'collapse') return false;
        if (cs.display === 'none') return false;
        return true;
    }

    function MettaFocusTrap(rootEl, options) {
        this.root = rootEl;
        this.opts = options || {};
        this.opener = null;
        this.active = false;
        this._inertSiblings = [];
        this._onKeydown = this._onKeydown.bind(this);
        if (!this.root.hasAttribute('tabindex')) {
            this.root.setAttribute('tabindex', '-1');
        }
    }

    MettaFocusTrap.prototype.activate = function (overrides) {
        if (this.active) return;
        this.active = true;
        var opts = overrides || {};
        this.opener = opts.opener || document.activeElement;
        this._activateOpts = opts;
        this._applyInert();

        var pickedSpec = opts.initialFocus || this.opts.initialFocus;
        var desired = this._findDesired(pickedSpec);
        var initial = this._resolveInitialFocus(pickedSpec);
        this._tryFocus(initial);
        document.addEventListener('keydown', this._onKeydown, true);

        // If the caller's preferred target exists in the DOM but didn't end up
        // focused (e.g., visibility:hidden during a CSS transition), retry on
        // the next transitionend bubbling out of the root.
        if (desired && document.activeElement !== desired) {
            var self = this;
            this._onTransitionEnd = function () {
                self.root.removeEventListener('transitionend', self._onTransitionEnd);
                self._onTransitionEnd = null;
                if (!self.active) return;
                if (desired && isVisible(desired) && document.activeElement !== desired) {
                    self._tryFocus(desired);
                }
            };
            this.root.addEventListener('transitionend', this._onTransitionEnd, { once: true });
        }
    };

    MettaFocusTrap.prototype._findDesired = function (spec) {
        if (!spec) return null;
        if (typeof spec === 'string') return this.root.querySelector(spec);
        if (spec instanceof HTMLElement) return spec;
        return null;
    };

    MettaFocusTrap.prototype.deactivate = function () {
        if (!this.active) return;
        this.active = false;
        document.removeEventListener('keydown', this._onKeydown, true);
        if (this._onTransitionEnd) {
            this.root.removeEventListener('transitionend', this._onTransitionEnd);
            this._onTransitionEnd = null;
        }
        this._releaseInert();
        this._restoreFocus();
        this.opener = null;
        this._activateOpts = null;
    };

    MettaFocusTrap.prototype._tryFocus = function (el) {
        if (el && typeof el.focus === 'function') {
            el.focus();
        } else {
            this.root.focus();
        }
    };

    MettaFocusTrap.prototype._resolveInitialFocus = function (override) {
        var picked = override || this.opts.initialFocus;
        if (picked) {
            if (typeof picked === 'string') {
                var found = this.root.querySelector(picked);
                if (found && isVisible(found)) return found;
            } else if (picked instanceof HTMLElement && isVisible(picked)) {
                return picked;
            }
        }
        var focusable = this._getFocusable();
        return focusable.length ? focusable[0] : this.root;
    };

    MettaFocusTrap.prototype._getFocusable = function () {
        var nodes = this.root.querySelectorAll(FOCUSABLE_SELECTOR);
        var out = [];
        for (var i = 0; i < nodes.length; i++) {
            if (isVisible(nodes[i])) out.push(nodes[i]);
        }
        return out;
    };

    MettaFocusTrap.prototype._applyInert = function () {
        var node = this.root;
        while (node && node.parentNode && node.parentNode !== document) {
            var parent = node.parentNode;
            for (var i = 0; i < parent.children.length; i++) {
                var sibling = parent.children[i];
                if (sibling === node) continue;
                if (sibling.hasAttribute('inert')) continue;
                sibling.setAttribute('inert', '');
                sibling.setAttribute(INERT_FLAG, '');
                this._inertSiblings.push(sibling);
            }
            node = parent;
        }
    };

    MettaFocusTrap.prototype._releaseInert = function () {
        for (var i = 0; i < this._inertSiblings.length; i++) {
            var sibling = this._inertSiblings[i];
            if (sibling.hasAttribute(INERT_FLAG)) {
                sibling.removeAttribute('inert');
                sibling.removeAttribute(INERT_FLAG);
            }
        }
        this._inertSiblings = [];
    };

    MettaFocusTrap.prototype._restoreFocus = function () {
        var opener = this.opener;
        if (opener && document.contains(opener) && typeof opener.focus === 'function') {
            opener.focus();
        }
    };

    MettaFocusTrap.prototype._onKeydown = function (e) {
        if (!this.active) return;

        if (e.key === 'Escape' || e.key === 'Esc') {
            e.preventDefault();
            if (typeof this.opts.onEscape === 'function') this.opts.onEscape(e);
            return;
        }

        if (e.key !== 'Tab') return;

        var focusable = this._getFocusable();
        if (!focusable.length) {
            e.preventDefault();
            this.root.focus();
            return;
        }

        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        var current = document.activeElement;

        if (e.shiftKey && (current === first || !this.root.contains(current))) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && (current === last || !this.root.contains(current))) {
            e.preventDefault();
            first.focus();
        }
    };

    window.MettaFocusTrap = MettaFocusTrap;
})();
